/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-unused-vars */
import { ExpandLessRounded, ExpandMoreRounded } from '@mui/icons-material';
import {
  Box,
  ButtonBase, List, Popover, Tooltip, Typography, useTheme
} from '@mui/material';
import React, { useState } from 'react';
import MenuItem from './MenuItem';
import SidebarMenuSubItem from './SidebarMenuSubItem';

interface SidebarMenuItemProps {
  menuItem: MenuItem;
  navOpen: boolean;
  bigDevice: boolean;
  // eslint-disable-next-line no-unused-vars
  onRouteSelected: (menuItem: MenuItem) => void;
}

const SidebarMenuItem: React.FC<React.PropsWithChildren<SidebarMenuItemProps>> = (props) => {
  const {
    bigDevice, menuItem, navOpen, onRouteSelected,
  } = props;
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const [anchorEl, setAnchorEl] = useState<any | null>(null);

  const hasChildren = menuItem.menuItems && menuItem.menuItems.length > 0;
  const allChildrenHidden = menuItem.menuItems && !menuItem.menuItems.find((a) => !a.hidden);
  const compact = !navOpen && bigDevice;

  const handleRouteSelected = typeof menuItem.route === 'string' ? onRouteSelected : menuItem.route;

  const _handleButtonClicked = (event: any) => {
    if (hasChildren) {
      setExpanded((oldVal) => !oldVal);
      setAnchorEl(event.currentTarget);
    } else {
      handleRouteSelected(menuItem);
    }
  };

  const handlePopoverClose = () => {
    setExpanded((oldVal) => !oldVal);
    setAnchorEl(null);
  };

  const _closeParent = () => {
    if (compact) {
      setExpanded((old) => !old);
    }
  };

  const submenuList = (
    <List
      sx={{
        ...(compact && {
          height: 'auto',
          margin: `${theme.spacing(0)} ${theme.spacing(2)}`,
          padding: `${theme.spacing(1)} ${theme.spacing(0)}`,
        }),
      }}
    >
      {menuItem.menuItems?.map(
        (menu) => !menu.hidden && (
          <SidebarMenuSubItem
            data-testid={menu.title}
            key={menu.title}
            menuItem={menu}
            // eslint-disable-next-line no-shadow
            onRouteSelected={onRouteSelected}
            collapse={Boolean(hasChildren && expanded && compact)}
            closeParent={() => _closeParent()}
          />
        ),
      )}
    </List>
  );

  if (hasChildren && allChildrenHidden) {
    return (
      // eslint-disable-next-line react/jsx-no-useless-fragment
      (<></>)
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: 'auto',
        position: 'relative',
      }}
    >
      <ButtonBase
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          height: theme.spacing(6),
          borderRadius: theme.spacing(0.5),
          padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
          position: 'relative',
          color: theme.palette.text.primary,
          '&:hover': {
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.primary.main,
          },
          ...(menuItem.isActive && {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.selected,
            fontWeight: 'bold',
          }),
          ...((menuItem.title === 'Settings' && !menuItem.isActive) && {
            color: theme.palette.text.disabled,
          }),
          ...(compact && {
            width: theme.spacing(6),
          }),
        }}
        onClick={(e) => _handleButtonClicked(e)}
      >
        {compact ? (
          <Tooltip title={menuItem.title} placement="right-start">
            <Box
              component="span"
              sx={{
                alignContent: 'center',
                display: 'inline-flex',
                flexGrow: 0,
                height: '100%',
              }}
            >
              {menuItem.leftIcon}
            </Box>
          </Tooltip>
        ) : (
          <Box
            component="span"
            sx={{
              alignContent: 'center',
              display: 'inline-flex',
              flexGrow: 0,
              height: '100%',
            }}
          >
            {menuItem.leftIcon}
          </Box>
        )}
        {!compact && (
          <Typography
            sx={{
              flexGrow: 1,
              paddingLeft: theme.spacing(4),
              fontWeight: 'bold',
            }}
            component="span"
            variant="subtitle2"
            align="left"
          >
            {menuItem.title}
          </Typography>
        )}

        {!compact && hasChildren && !expanded && (
          <Box
            component="span"
            sx={{
              alignContent: 'center',
              display: 'inline-flex',
              flexGrow: 0,
              height: '100%',
            }}
          >
            <ExpandMoreRounded />
          </Box>
        )}

        {!compact && hasChildren && expanded && (
          <Box
            component="span"
            sx={{
              alignContent: 'center',
              display: 'inline-flex',
              flexGrow: 0,
              height: '100%',
            }}
          >
            <ExpandLessRounded />
          </Box>
        )}
      </ButtonBase>

      {hasChildren
        && expanded
        && (compact ? (
          <Popover
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            anchorEl={anchorEl}
            onClose={() => handlePopoverClose()}
            open={expanded && compact}
          >
            {submenuList}
          </Popover>
        ) : (
          submenuList
        ))}
    </Box>
  );
};

export default SidebarMenuItem;
